/**
 * Typed GA4 event layer for the farmer SPA (ROPS-6 / CMO Instrumentacao Spec B).
 *
 * The public site already owns Visit / Engage / Intent via GTM. This module
 * exists for one client-side conversion: `sign_up`, fired when Clerk sign-up
 * *completes* — not when `/sign-up` is viewed. CMO Spec B supersedes ADR
 * `2026-08-15-ga4-measurement-boundary` decision A (no Google tag on the SPA)
 * for this event only; later funnel stages still arrive server-side as
 * `trial_start` / `first_record_created` / `subscription_paid`.
 *
 * Same GTM container as ruralops.com (`GTM-TVMLJ9WP` → property 548148755).
 * Consent Mode v2 stays denied until the public-site decision is replayed.
 * `dataLayer` pushes are unconditional so a grant that lands mid-session is
 * measured from that moment on — identical to `frontend-public` `track()`.
 *
 * Do not add `contact_request` here. That event lives on the public site and
 * is owned in GA4 by Wanderson.
 */

export type AnalyticsEvent = {
	event: "sign_up";
	/** GA4 recommended parameter. Omit rather than guess. */
	method?: string;
};

declare global {
	interface Window {
		dataLayer?: unknown[];
		gtag?: (...args: unknown[]) => void;
	}
}

/**
 * Every parameter key the SPA's GA4 tag may read. Written on every push with
 * unused keys `undefined` so a later event cannot inherit a retained Data
 * Layer Variable — same full-shape rule as the public site.
 */
export const ANALYTICS_PARAM_KEYS = ["method"] as const;

/**
 * Push an event to the GTM data layer.
 *
 * No-op when `window.dataLayer` is absent — the container is only injected in
 * builds that configure `VITE_GTM_CONTAINER_ID`, so localdev and stage have
 * no data layer at all. The array is deliberately **not** created here.
 */
export function track(event: AnalyticsEvent): void {
	const dataLayer =
		typeof window === "undefined" ? undefined : window.dataLayer;
	if (!Array.isArray(dataLayer)) return;
	const cleared = Object.fromEntries(
		ANALYTICS_PARAM_KEYS.map((key) => [key, undefined]),
	);
	dataLayer.push({ ...cleared, ...event });
}
